import type { PlatinumData } from '../types/carbon/models/PlatinumData'

/**
 * Skip zero-base points (#1181).
 *
 * A chart plots a zero wherever the base (`Cases WF`) is 0, drawing a flat
 * zero line through periods where no data exists at all. This blanks those
 * points so the line starts at the first REAL data point.
 *
 * Each data row is governed by the nearest base row ABOVE it in the side
 * axis (Carbon lays a group's base first — position 0 of a charted table is
 * a base row). For every column where that base's frequency is 0, the data
 * cell's three measures are set to NaN.
 *
 * NaN is deliberate, not a hack around a missing null: it must survive
 * selection filtering (which moves cells without reading them) and reach
 * convertPlatinumToTable, which turns it into '' — and toNumber('') is null,
 * which is exactly the gap ECharts leaves. A real 0 with a live base stays a
 * plotted 0; only base-less zeros disappear. Rows above the first base row,
 * and tables with no base rows at all, are left untouched.
 *
 * Pure: returns a new object, never mutates the input.
 */
export function blankZeroBasePoints(data: PlatinumData): PlatinumData {
  const sideVecs = data.side?.vecs
  const rows = data.cells?.rows
  if (!sideVecs || !rows) return data

  const isBase = (index: number) =>
    (sideVecs[index]?.type || '').toString().toLowerCase() === 'base'

  // Nothing to govern with — leave the table exactly as it was.
  if (!sideVecs.some((_, i) => isBase(i))) return data

  let governingBaseRow = -1
  let changed = false
  const newRows = rows.map((row, rowIndex) => {
    if (isBase(rowIndex)) {
      governingBaseRow = rowIndex
      return row
    }
    if (governingBaseRow < 0) return row
    const baseCells = rows[governingBaseRow]?.cell || []
    const cells = row.cell || []
    let rowChanged = false
    const newCells = cells.map((cell, colIndex) => {
      const baseFreq = baseCells[colIndex]?.freq ?? 0
      if (baseFreq !== 0 || !cell) return cell
      rowChanged = true
      return { ...cell, freq: NaN, colpc: NaN, rowpc: NaN }
    })
    if (!rowChanged) return row
    changed = true
    return { ...row, cell: newCells }
  })

  if (!changed) return data
  return { ...data, cells: { ...data.cells, rows: newRows } }
}
